import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import api, { TOKEN_KEY } from '../api/client';

const AuthContext = createContext(null);

// Единый провайдер: хранит user любого вида (admin/seller/customer).
// Для обратной совместимости отдельно экспортируется useAuth (даёт admin)
// и useCustomerAuth (даёт customer) — см. CustomerAuthContext.js
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) { setLoading(false); return; }
    api.get('/auth/me')
      .then(res => setUser(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Интерсептор клиента сбрасывает протухший токен и шлёт это событие
  useEffect(() => {
    const onLogout = () => setUser(null);
    window.addEventListener('auth:logout', onLogout);
    return () => window.removeEventListener('auth:logout', onLogout);
  }, []);

  // Универсальный логин: бэкенд сам ищет admin/seller/customer
  const login = async (login, password) => {
    const res = await api.post('/auth/login', { login, password });
    localStorage.setItem(TOKEN_KEY, res.data.token);
    setUser(res.data.user);
    return res.data;
  };

  // Регистрация покупателя (роль фиксированная — customer)
  const register = async (email, password, name, phone, consent_version) => {
    const res = await api.post('/customers/register', { email, password, name, phone, consent_version });
    localStorage.setItem(TOKEN_KEY, res.data.token);
    setUser({ kind: 'customer', ...res.data.customer });
    return res.data;
  };

  const logout = () => { localStorage.removeItem(TOKEN_KEY); setUser(null); };
  // Устаревшее: клиент подставляет токен сам. Оставлено на случай прямых вызовов axios.
  const getAuthHeader = useCallback(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);
  const updateUser = (patch) => setUser(prev => prev ? { ...prev, ...patch } : prev);

  // Совместимость со старыми компонентами:
  const isStaff = user && (user.kind === 'admin' || user.kind === 'seller');
  const admin = isStaff ? user : null;
  const customer = user && user.kind === 'customer' ? user : null;
  const updateAdmin = updateUser;
  const updateCustomer = updateUser;

  return (
    <AuthContext.Provider value={{
      user, loading, login, register, logout, getAuthHeader, updateUser,
      // legacy:
      admin, customer, updateAdmin, updateCustomer,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
